import { useState, useMemo } from 'react';
import {
  SlideOverDrawer,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/slide-over-drawer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { usePortOrderStore } from '../stores/port-order-store';
import { formatPhoneNumber } from '@/lib/utils';
import { Search, Download } from 'lucide-react';
import toast from 'react-hot-toast';

export function NumberManifestDrawer() {
  const { manifestPortId, manifestDids, isManifestOpen, closeManifest } =
    usePortOrderStore();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter DIDs based on search term
  const filteredDids = useMemo(() => {
    if (!searchTerm.trim()) return manifestDids;
    const normalizedSearch = searchTerm.replace(/\D/g, '');
    return manifestDids.filter((did) => did.includes(normalizedSearch));
  }, [manifestDids, searchTerm]);

  const handleExportCSV = () => {
    if (!manifestDids || manifestDids.length === 0) {
      toast.error('No numbers to export');
      return;
    }

    const csvContent = [
      'Phone Number',
      ...manifestDids.map((did) => formatPhoneNumber(did)),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `port-${manifestPortId}-numbers.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${manifestDids.length} numbers to CSV`);
  };

  const handleClose = () => {
    setSearchTerm('');
    closeManifest();
  };

  if (!manifestPortId) return null;

  return (
    <SlideOverDrawer isOpen={isManifestOpen} onClose={handleClose} width="md">
      {/* Header */}
      <DrawerHeader
        title={`Numbers in Port #PORT-${String(manifestPortId).padStart(5, '0')}`}
        onClose={handleClose}
      />

      {/* Content */}
      <DrawerBody className="flex flex-col">
        {/* Search and Export Row */}
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              placeholder="Search numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="secondary" onClick={handleExportCSV} className="flex-shrink-0">
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Results count */}
        <div className="px-4 py-2 bg-slate-50 text-xs text-slate-500 border-b border-slate-100">
          {searchTerm.trim() ? (
            <>
              Showing {filteredDids.length} of {manifestDids.length} numbers
            </>
          ) : (
            <>{manifestDids.length} numbers in this port order</>
          )}
        </div>

        {/* Number List */}
        <div className="flex-1 overflow-y-auto">
          {filteredDids.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm.trim()
                ? 'No numbers match your search'
                : 'No numbers in this port order'}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredDids.map((did, index) => (
                <div
                  key={did}
                  className="px-4 py-3 text-sm font-mono text-indigo-600 hover:bg-slate-50 animate-fade-in"
                  style={{ animationDelay: `${index * 20}ms` }}
                >
                  {formatPhoneNumber(did)}
                </div>
              ))}
            </div>
          )}
        </div>
      </DrawerBody>
    </SlideOverDrawer>
  );
}
